import { CabinClassCode } from '@constants/cabin.constants';
import { DisplayViewOptionsEnum } from '@constants/flights.constants';
import { TripType } from '@constants/home.constants';
import { Booking } from '@models/booking.models';
import {
  RouteScheduleDateResponse,
  RouteScheduleRequest,
} from '@models/requests/route-schedule-request.model';
import { LowFareSummaryRequest, LowFareMonthyByOrigin, PassengerCriteria, SearchRequest, LowFareMonthyByDestination } from '@models/requests/search-request.model';
import { N0SellKey } from '@models/requests/trip-sell-request.model';
import {
  N0AvailabilityResponse,
  N0AvailableJourney,
  N0LowFareAvailabilityByDestinationResponse,
  N0LowFareAvailabilityByOriginResponse,
  N0LowFareAvailabilitySummaryByOriginListResponse,
} from '@models/responses/availability-response.model';
import { N0LowFareTrip } from '@models/responses/lowfare-availability-response.model';
import { Station } from '@models/responses/station-response.model';
import { createAction, props } from '@ngrx/store';
import { DateTime } from 'luxon';
import { MetaId } from "@models/meta-id.model";

export const enum AvailabilityActionTypes {
  getAvailability = '[availability] GET_AVAILABILITY',
  getLowFareAvailability = '[availability] GET_LOW_FARE_AVAILABILITY',
  getRouteSchedule = '[availability] GET_ROUTE_SCHEDULE',
  getRouteScheduleSuccess = '[availability] GET_ROUTE_SCHEDULE_SUCCESS',
  redirectToDohop = '[availability] REDIRECT_TO_DOHOP',
  searchAll = '[availability] SEARCH_ALL',
  searchAllHome = '[availability] SEARCH_ALL_HOME',
  searchAllModify = '[availability] SEARCH_ALL_MODIFY',
  searchByQueryParams = '[availability] SEARCH_BY_QUERY_PARAMS',
  invalidSearchByQueryParams = '[availability] INVALID_SEARCH_BY_QUERY_PARAMS',
  searchFares = '[availability] SEARCH_FARES',
  searchFromDestinations = '[availability] SEARCH_FROM_DESTINATIONS',
  searchFromDestinationsWithSchedule = '[availability] SEARCH_FROM_DESTINATIONS_WITH_SCHEDULE',
  searchLowFares = '[availability] SEARCH_LOW_FARES',
  searchLowFaresMonthlyByOrigin = '[availability] SEARCH_LOW_FARES_MONTHLY_BY_ORIGIN',
  searchLowFaresMonthlyByOriginSuccess = '[availability] SEARCH_SEARCH_LOW_FARES_MONTHLY_BY_ORIGIN',
  searchLowFaresMonthlyByDestination = '[availability] SEARCH_LOW_FARES_MONTHLY_BY_DESTINATION',
  searchLowFaresMonthlyByDestinationSuccess = '[availability] SEARCH_SEARCH_LOW_FARES_MONTHLY_BY_DESTINATION',
  searchFareAvailabilitySuccess = '[availability] SEARCH_FARE_AVAILABILITY_SUCCESS',
  searchLowFareAvailabilitySuccess = '[availability] SEARCH_LOW_FARE_AVAILABILITY_SUCCESS',
  storeAvailabilitySelection = '[availability] STORE_AVAILABILITY_SELECTION',
  storeAvailabilitySelections = '[availability] STORE_AVAILABILITY_SELECTIONS',
  clearAvailabilitySelection = '[availability] CLEAR_AVAILABILITY_SELECTION',
  sellTripSelection = '[availability] SELL_TRIP_SELECTION',
  sellTripSelectionSuccess = '[availability] SELL_TRIP_SELECTION_SUCCESS',
  sellTripSelectionByQueryParams = '[availability] SELL_TRIP_SELECTION_BY_QUERY_PARAMS',
  setAvailabilityDisplayViewState = '[availability] SET_AVAILABILITY_DISPLAY_VIEW_STATE',
  searchRequestUpdate = '[availability] SEARCH_INPUT_UPDATE_REQUEST',
  setShowLowFareSelectedDates = '[availability] SET_SHOW_LOW_FARE_SELECTED_DATES',
  setPreferredCabin = '[availability] SET_PREFERRED_CABIN',
  setIsSearchByQueryState = '[availability] SET_IS_SEARCH_BY_QUERY_STATE',
  convertToOneWay = '[availability] CONVERT_TO_ONE_WAY',
  convertToOneWaySearch = '[availability] CONVERT_TO_ONE_WAY_SEARCH',
  convertToOneWaySuccess = '[availability] CONVERT_TO_ONE_WAY_SUCCESS',
  changePassengers = '[availability] CHANGE_PASSENGERS',
  clearPromoCode = '[availability] CLEAR_PROMO_CODE',
  updateCalendarSearchCriteria = '[availability] UPDATE_CALENDAR_SEARCH_CRITERIA',
  updateCalendarWeekNumber = '[availability] UPDATE_CALENDAR_WEEK_NUMBER',
  selectCalendarDate = '[availability] SELECT_CALENDAR_DATE',
  setFullCalendarView = '[availability] SET_FULL_CALENDAR_VIEW',
  setSearchTripType = '[availability] SET_SEARCH_TRIP_TYPE',
  selectCalendarDay = '[availability] SELECT_CALENDAR_DAY',
  backToCalendar = '[availability] BACK_TO_CALENDAR',
  searchLowFaresSummary = '[availability] SEARCH_LOW_FARES_SUMMARY',
  searchLowFaresSummarySuccess = '[availability] SEARCH_LOW_FARES_SUMMARY_SUCCESS',
  getBundlePrice = '[availability] GET_BUNDLE_PRICE',
  getBundlePriceSuccess = '[availability] GET_BUNDLE_PRICE_SUCCESS',
}

export const availabilitySearchAll = createAction(
  AvailabilityActionTypes.searchAll,
  props<{
    payload: SearchRequest;
    tripType: TripType;
    tripIndex?: number;
    clearBooking?: boolean;
  }>(),
);

export const availabilitySearchAllHome = createAction(
  AvailabilityActionTypes.searchAllHome,
  props<{
    payload: SearchRequest;
    tripType: TripType;
    tripIndex?: number;
    clearBooking?: boolean;
    isTravelersStart?: boolean;
    isSeatsBeforeBags?: boolean;
  }>(),
);

export const availabilityRedirectToDohop = createAction(
  AvailabilityActionTypes.redirectToDohop,
  props<{
    payload: SearchRequest;
    stations: Station[];
    tripType: TripType;
    tripIndex?: number;
  }>(),
);

export const availabilitySearchAllModify = createAction(
  AvailabilityActionTypes.searchAllModify,
  props<{
    payload: SearchRequest;
    tripType: TripType;
    tripIndex?: number;
    clearBooking?: boolean;
    isTravelersStart?: boolean;
  }>(),
);

export const availabilitySearchByQueryParams = createAction(
  AvailabilityActionTypes.searchByQueryParams,
  props<{
    payload: SearchRequest;
    tripType: TripType;
    tripIndex?: number;
    sourceOrganization?: string;
    redirectId?: string;
    isTravelersStart?: boolean;
    isSeatsBeforeBags?: boolean;
    metaId?: MetaId;
  }>(),
);

export const availabilitySearchFromDestinations = createAction(
  AvailabilityActionTypes.searchFromDestinations,
  props<{
    payload: SearchRequest;
    tripType: TripType;
    tripIndex?: number;
    isTravelersStart?: boolean;
    isSeatsBeforeBags?: boolean;
    clearBooking?: boolean;
  }>(),
);

export const availabilitySearchFromDestinationsWithSchedule = createAction(
  AvailabilityActionTypes.searchFromDestinationsWithSchedule,
  props<{
    payload: SearchRequest;
    tripType: TripType;
  }>(),
);

export const availabilityInvalidSearchByQueryParams = createAction(
  AvailabilityActionTypes.invalidSearchByQueryParams
)

export const availabilitySearchFares = createAction(
  AvailabilityActionTypes.searchFares,
  props<{
    payload: SearchRequest;
    tripIndex?: number;
    clearBooking?: boolean;
    clearSelectedFares?: number[];
    showSelectedOutboundDate?: boolean;
    showSelectedReturnDate?: boolean;
  }>(),
);

export const availabilitySearchLowFares = createAction(
  AvailabilityActionTypes.searchLowFares,
  props<{ payload: SearchRequest; tripIndex?: number; isCalendar?: boolean }>(),
);

export const availabilitySearchLowFaresMonthlyByOrigin = createAction(
  AvailabilityActionTypes.searchLowFaresMonthlyByOrigin,
  props<{ payload: LowFareMonthyByOrigin; }>(),
);

export const availabilitySearchLowFaresMonthlyByOriginSuccess = createAction(
  AvailabilityActionTypes.searchLowFaresMonthlyByOriginSuccess,
  props<{
    payload: N0LowFareAvailabilityByOriginResponse;
  }>(),
);

export const availabilitySearchLowFaresMonthlyByDestination = createAction(
  AvailabilityActionTypes.searchLowFaresMonthlyByDestination,
  props<{ payload: LowFareMonthyByDestination; }>(),
);

export const availabilitySearchLowFaresMonthlyByDestinationSuccess = createAction(
  AvailabilityActionTypes.searchLowFaresMonthlyByDestinationSuccess,
  props<{
    payload: N0LowFareAvailabilityByDestinationResponse;
  }>(),
);

export const availabilitySearchLowFaresSummary = createAction(
  AvailabilityActionTypes.searchLowFaresSummary,
  props<{ payload: LowFareSummaryRequest; }>(),
);

export const availabilitySearchLowFaresSummarySuccess = createAction(
  AvailabilityActionTypes.searchLowFaresSummarySuccess,
  props<{
    payload: N0LowFareAvailabilitySummaryByOriginListResponse;
  }>(),
);

export const availabilitySearchFareAvailabilitySuccess = createAction(
  AvailabilityActionTypes.searchFareAvailabilitySuccess,
  props<{
    payload: N0AvailabilityResponse | null;
    tripIndex?: number;
    clearBooking?: boolean;
    upgradeCabin?: boolean;
    selectionOnTripConfirmationView?: boolean;
    showStrikethroughPrice?: boolean;
  }>(),
);

export const availabilitySearchLowFareAvailabilitySuccess = createAction(
  AvailabilityActionTypes.searchLowFareAvailabilitySuccess,
  props<{ payload: N0LowFareTrip[]; tripIndex?: number }>(),
);

export const availabilityStoreAvailabilitySelection = createAction(
  AvailabilityActionTypes.storeAvailabilitySelection,
  props<{
    payload: N0SellKey;
    journey: N0AvailableJourney;
    index: number;
    displayView? : DisplayViewOptionsEnum
  }>(),
);

export const availabilityStoreAvailabilitySelections = createAction(
  AvailabilityActionTypes.storeAvailabilitySelections,
  props<{
    sellKeys: (N0SellKey | null)[];
    journeys: (N0AvailableJourney | null)[];
  }>(),
);

export const availabilitySellTripSelectionByQueryParams = createAction(
  AvailabilityActionTypes.sellTripSelectionByQueryParams,
  props<{
    payload: SearchRequest;
    tripType: TripType;
    sellKeys: N0SellKey[];
    tripIndex?: number;
    sourceOrganization?: string;
    isTravelersStart?: boolean;
    isSeatsBeforeBags?: boolean;
    redirect?: boolean
    metaId?: MetaId
  }>(),
);

export const availabilitySellTripSelection = createAction(
  AvailabilityActionTypes.sellTripSelection,
);

export const availabilitySellTripSelectionSuccess = createAction(
  AvailabilityActionTypes.sellTripSelectionSuccess,
  props<{ payload: Booking, isUpgraded?: boolean, redirect?: boolean }>(),
);

export const availabilityGetRouteScheduleSuccess = createAction(
  AvailabilityActionTypes.getRouteScheduleSuccess,
  props<{
    request: RouteScheduleRequest;
    schedule: RouteScheduleDateResponse[];
    fromDestination?: boolean;
    payload?: {
      payload: SearchRequest;
      tripType: TripType;
      tripIndex?: number;
      isTravelersStart?: boolean;
      clearBooking?: boolean;
    }
  }>(),
);

export const availabilityGetRouteSchedule = createAction(
  AvailabilityActionTypes.getRouteSchedule,
  props<{ request: RouteScheduleRequest }>(),
);

export const availabilityDisplayView = createAction(
  AvailabilityActionTypes.setAvailabilityDisplayViewState,
  props<{ mode: DisplayViewOptionsEnum }>(),
);

export const searchRequestUpdate = createAction(
  AvailabilityActionTypes.searchRequestUpdate,
  props<{ searchRequest: SearchRequest }>(),
);

export const showLowFareSelectedDates = createAction(
  AvailabilityActionTypes.setShowLowFareSelectedDates,
  props<{ show: boolean }>(),
);

export const setPreferredCabin = createAction(
  AvailabilityActionTypes.setPreferredCabin,
  props<{ cabinClass: CabinClassCode }>(),
);

export const clearAvailabilitySelection = createAction(
  AvailabilityActionTypes.clearAvailabilitySelection,
);

export const setIsSearchByQueryState = createAction(
  AvailabilityActionTypes.setIsSearchByQueryState,
  props<{ state: boolean }>(),
);

export const setSearchTripType = createAction(
  AvailabilityActionTypes.setSearchTripType,
  props<{ tripType: TripType; }>(),
);

export const availabilityConvertToOneWay = createAction(
  AvailabilityActionTypes.convertToOneWay,
);

export const availabilityConvertToOneWaySearch = createAction(
  AvailabilityActionTypes.convertToOneWaySearch,
  props<{
    payload: SearchRequest;
    tripType: TripType;
    tripIndex?: number;
    clearBooking?: boolean;
    isTravelersStart?: boolean;
  }>(),
);

export const availabilityConvertToOneWaySuccess = createAction(
  AvailabilityActionTypes.convertToOneWaySuccess,
  props<{
    payload: N0AvailabilityResponse | null;
    tripIndex?: number;
    clearBooking?: boolean;
    upgradeCabin?: boolean;
    selectionOnTripConfirmationView?: boolean;
    showStrikethroughPrice?: boolean;
  }>(),
);

export const availabilityChangePassengers = createAction(
  AvailabilityActionTypes.changePassengers,
  props<{ passengers: PassengerCriteria[] }>(),
);

export const availabilityClearPromoCode = createAction(
  AvailabilityActionTypes.clearPromoCode,
);


export const updateCalendarSearchCriteria = createAction(
  AvailabilityActionTypes.updateCalendarSearchCriteria,
  props<{ beginDate: DateTime, tripIndex: number, activeWeek?: number }>()
)

export const updateCalendarWeekNumber = createAction(
  AvailabilityActionTypes.updateCalendarWeekNumber,
  props<{ weekNumber: number }>()
)

export const selectCalendarDate = createAction(
  AvailabilityActionTypes.selectCalendarDate,
  props<{ beginDate: DateTime, weekNumber: number, tripIndex: number }>()
)

export const selectCalendarDay = createAction(
  AvailabilityActionTypes.selectCalendarDay,
  props<{ beginDate: DateTime, tripIndex: number }>()
)


export const availabilityBackToCalendar = createAction(
  AvailabilityActionTypes.backToCalendar
);

export const availabilityGetBundlePrice = createAction(
  AvailabilityActionTypes.getBundlePrice
);

export const availabilityGetBundlePriceSuccess = createAction(
  AvailabilityActionTypes.getBundlePriceSuccess,
  props<{ price: number }>()
);
